import { range } from 'lodash-es';
import { type StateNodeConfig } from '#node_modules/xstate/dist/xstate.cjs';
import {
  type MachineContextType,
  type MachineEventsType,
} from '#src/state-machine/types';
import { type DatasetData, type SummaryData, type GeoData } from '#src/types';

export const withYearsStepperDefinition: StateNodeConfig<
  MachineContextType,
  MachineEventsType,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any
> = {
  initial: 'idle',
  on: {
    YEARS_STEPPER_PLAY: {
      target: '.idle',
    },
  },
  states: {
    idle: {
      on: {
        YEARS_STEPPER_PLAY: {
          target: 'prefetchYearsData',
          actions: 'markInteracted',
        },
      },
    },
    prefetchYearsData: {
      tags: ['playing', 'loading'],
      invoke: {
        src: 'fetchData',
        input: ({ context }) => {
          const isLastYear = context.data.yearsOptions.filter(
            (y) => y > context.selectedYears[0]
          );
          const yearsOptionsLength = context.data.yearsOptions.length;
          const selectedYears = isLastYear
            ? context.data.yearsOptions
            : range(
                context.selectedYears[0],
                context.data.yearsOptions[yearsOptionsLength - 1] + 1
              );
          return {
            context: {
              ...context,
              yearsMode: 'multiple',
              selectedYears,
            },
            dataset: context.dataset,
          };
        },
        onDone: {
          target: 'loopYears',
          actions: 'saveYearsStepperData',
        },
      },
    },
    loopYears: {
      tags: ['playing'],
      after: {
        1000: [
          {
            guard: 'isNotLastYear',
            target: 'loopYears',
            reenter: true,
            actions: 'updateYearsStepper',
          },
          {
            target: 'idle',
          },
        ],
      },
    },
  },
};

export function updateYearsStepper(
  context: MachineContextType,
  targetYear: number
): {
  nextYearDatasetData: DatasetData;
  upcomingYearsDatasetData: DatasetData;
  nextYearSummary: SummaryData | null;
  upcomingYearsSummary: SummaryData | null;
  nextYearMapChartData: DatasetData | null;
  upcomingYearsMapChartData: DatasetData | null;
  nextYearGeoData: GeoData | null;
  upcomingYearsGeoData: GeoData | null;
} {
  const nextYearDatasetData: DatasetData =
    context.data.yearsStepper?.[0]?.filter(
      (d) => d.date.getUTCFullYear() === targetYear
    ) as DatasetData;
  const upcomingYearsDatasetData: DatasetData =
    context.data.yearsStepper?.[0]?.filter(
      (d) => d.date.getUTCFullYear() > targetYear
    ) as DatasetData;

  const nextYearSummary: SummaryData | null =
    context.data.yearsStepper?.[1]?.filter((d) => d.year === targetYear) ??
    null;
  const upcomingYearsSummary: SummaryData | null =
    context.data.yearsStepper?.[1]?.filter((d) => d.year > targetYear) ?? null;

  const nextYearMapChartData: DatasetData | null =
    context.data.yearsStepper?.[2]?.filter(
      (d) => d.date.getUTCFullYear() === targetYear
    ) ?? null;
  const upcomingYearsMapChartData: DatasetData | null =
    context.data.yearsStepper?.[2]?.filter(
      (d) => d.date.getUTCFullYear() > targetYear
    ) ?? null;

  let nextYearGeoData: GeoData | null = null;
  let upcomingYearsGeoData: GeoData | null = null;

  if (context.data.yearsStepper?.[3]) {
    nextYearGeoData = context.data.yearsStepper[3].filter(
      (d) => d.year === targetYear
    );
    upcomingYearsGeoData = context.data.yearsStepper[3].filter(
      (d) => d.year > targetYear
    );
  }

  return {
    nextYearDatasetData,
    nextYearSummary,
    nextYearMapChartData,
    nextYearGeoData,
    upcomingYearsDatasetData,
    upcomingYearsSummary,
    upcomingYearsMapChartData,
    upcomingYearsGeoData,
  };
}
